import React, { useState } from 'react';
import { Search, Edit2, Trash2 } from 'lucide-react';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import './QuestionBank.css';

const QuestionBank: React.FC = () => {
  const { questions } = useAssessmentManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [selectedType, setSelectedType] = useState('All Types');
  const [showAddModal, setShowAddModal] = useState(false);
  const [questionType, setQuestionType] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState<number | null>(null);

  const getTypeClass = (type: string) => {
    const typeMap: Record<string, string> = {
      'MCQ': 'trainer-assessment-type-mcq',
      'T/F': 'trainer-assessment-type-tf',
      'Essay': 'trainer-assessment-type-essay',
      'Short': 'trainer-assessment-type-short'
    };
    return typeMap[type] || '';
  };

  const getDifficultyClass = (difficulty: string) => {
    return `trainer-assessment-difficulty-${difficulty}`;
  };

  return (
    <div className="trainer-assessment-question-bank">
      <div className="trainer-assessment-bank-header">
        <h3 className="trainer-assessment-bank-title">Question Repository</h3>
        <button className="trainer-assessment-add-question-btn" onClick={() => setShowAddModal(true)}>
          + Add Question
        </button>
      </div>

      <div className="trainer-assessment-bank-filters">
        <div className="trainer-assessment-search-wrapper">
          <Search size={18} className="trainer-assessment-search-icon" />
          <input
            type="text"
            placeholder="Search Question"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="trainer-assessment-search-input"
          />
        </div>
        <select
          className="trainer-assessment-filter-dropdown"
          value={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value)}
        >
          <option>All Topics</option>
          <option>Leadership</option>
          <option>Programming</option>
          <option>Technical</option>
          <option>Project Management</option>
        </select>
        <select
          className="trainer-assessment-filter-dropdown"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <option>All Types</option>
          <option>MCQ</option>
          <option>T/F</option>
          <option>Essay</option>
          <option>Short</option>
        </select>
      </div>

      <div className="trainer-assessment-questions-list">
        {questions?.map((q) => (
          <div key={q.id} className="trainer-assessment-question-item">
            <div className="trainer-assessment-question-item-header">
              <div className="trainer-assessment-question-badges">
                <span className={`trainer-assessment-type-badge ${getTypeClass(q.type)}`}>{q.type}</span>
                <span className={`trainer-assessment-difficulty-badge ${getDifficultyClass(q.difficulty)}`}>
                  {q.difficulty}
                </span>
                <span className="trainer-assessment-topic-badge">{q.topic}</span>
                <span className="trainer-assessment-points-badge">{q.points} pts</span>
              </div>
              <div className="trainer-assessment-question-actions">
                <button className="trainer-assessment-icon-btn trainer-assessment-icon-btn-edit">
                  <Edit2 size={16} />
                </button>
                <button className="trainer-assessment-icon-btn trainer-assessment-icon-btn-delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <p className="trainer-assessment-question-text">{q.question}</p>
            {q.options && (
              <div className="trainer-assessment-question-options-grid">
                {q.options.map((option, idx) => (
                  <div
                    key={idx}
                    className={`trainer-assessment-option-item ${option === q.correctAnswer ? 'trainer-assessment-correct-answer' : ''}`}
                  >
                    {option}
                    {option === q.correctAnswer && (
                      <span className="trainer-assessment-correct-indicator">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="trainer-assessment-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="trainer-assessment-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-assessment-modal-header">
              <div className="trainer-assessment-modal-title-wrapper">
                <span className="trainer-assessment-modal-icon">?</span>
                <h3>Create New Question</h3>
              </div>
              <button className="trainer-assessment-modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="trainer-assessment-modal-body">
              <div className="trainer-assessment-form-row">
                <div className="trainer-assessment-form-field">
                  <label>Question Type</label>
                  <select
                    className="trainer-assessment-form-select"
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="MCQ">MCQ</option>
                    <option value="T/F">T/F</option>
                    <option value="Essay">Essay</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="trainer-assessment-form-field">
                  <label>Topic</label>
                  <select className="trainer-assessment-form-select">
                    <option>Select</option>
                    <option>Leadership</option>
                    <option>Programming</option>
                    <option>Technical</option>
                  </select>
                </div>
                <div className="trainer-assessment-form-field">
                  <label>Difficulty</label>
                  <select className="trainer-assessment-form-select">
                    <option>Select</option>
                    <option>Easy</option>
                    <option>Medium</option>
                    <option>Hard</option>
                  </select>
                </div>
              </div>

              <div className="trainer-assessment-toggle-field">
                <span>Make Question mandatory?</span>
                <label className="trainer-assessment-toggle">
                  <input type="checkbox" />
                  <span className="trainer-assessment-toggle-slider"></span>
                </label>
              </div>

              <div className="trainer-assessment-form-field">
                <label>Question</label>
                <textarea
                  className="trainer-assessment-form-textarea"
                  placeholder="Enter your question..."
                  rows={3}
                />
              </div>

              {(questionType === 'MCQ' || questionType === 'T/F') && (
                <div className="trainer-assessment-form-field">
                  <label>Options (Click radio button to mark correct answer)</label>
                  <div className="trainer-assessment-options-list">
                    {options.map((option, idx) => (
                      <div key={idx} className="trainer-assessment-option-input-row">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={correctAnswerIndex === idx}
                          onChange={() => setCorrectAnswerIndex(idx)}
                          className="trainer-assessment-radio-input"
                        />
                        <input
                          type="text"
                          placeholder={`Option ${idx + 1}`}
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...options];
                            newOptions[idx] = e.target.value;
                            setOptions(newOptions);
                          }}
                          className="trainer-assessment-option-input"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="trainer-assessment-form-field">
                <label>Points</label>
                <input type="number" className="trainer-assessment-form-input" defaultValue={10} />
              </div>
            </div>
            <div className="trainer-assessment-modal-footer">
              <button className="trainer-assessment-btn-save">
                <span>💾</span> Save Question
              </button>
              <button className="trainer-assessment-btn-cancel" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
